import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Shield, CheckCircle2, Circle, AlertTriangle, Clock,
  Loader2, ChevronDown, ChevronUp, FileCheck, User, DollarSign,
  Calendar, Briefcase, Target, AlertCircle
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Qualification Panel Component
export function QualificationPanel({ leadId, qualification, onUpdate }) {
  const { token } = useAuth();
  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(qualification || {});

  useEffect(() => {
    fetchOptions();
  }, []);

  useEffect(() => {
    setData(qualification || {});
  }, [qualification]);

  const fetchOptions = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/leads/compliance/options`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOptions(response.data);
    } catch (error) {
      console.error('Failed to fetch options:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_BASE}/api/leads/compliance/lead/${leadId}/qualification`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Qualification updated');
      onUpdate?.();
    } catch (error) {
      toast.error('Failed to update qualification');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="py-8 text-center">
        <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#4A3B32]" />
      </div>
    );
  }

  const score = data.qualificationScore || 0;
  const scoreColor = score >= 70 ? 'text-green-600' : score >= 40 ? 'text-amber-600' : 'text-gray-500';

  return (
    <div className="space-y-6">
      {/* Score Display */}
      <div className="flex items-center justify-between p-4 bg-[#F9F7F5] rounded-lg">
        <div>
          <p className="text-sm text-[#5D524D]">Qualification Score</p>
          <p className={cn("text-3xl font-bold", scoreColor)}>{score}</p>
        </div>
        <div className="w-24 h-24 relative">
          <svg className="w-full h-full transform -rotate-90">
            <circle
              cx="48"
              cy="48"
              r="40"
              stroke="#E6E0D9"
              strokeWidth="8"
              fill="none"
            />
            <circle
              cx="48"
              cy="48"
              r="40"
              stroke={score >= 70 ? '#10B981' : score >= 40 ? '#F59E0B' : '#9CA3AF'}
              strokeWidth="8"
              fill="none"
              strokeDasharray={`${(score / 100) * 251.2} 251.2`}
              strokeLinecap="round"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <Target className={cn("w-6 h-6", scoreColor)} />
          </div>
        </div>
      </div>

      {/* Qualification Fields */}
      <div className="grid grid-cols-2 gap-4">
        {/* Wealth Band */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-[#8C8078]" />
            Estimated Assets
          </Label>
          <Select
            value={data.wealthBand || ''}
            onValueChange={(v) => setData(prev => ({ ...prev, wealthBand: v }))}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select range" />
            </SelectTrigger>
            <SelectContent>
              {options?.wealthBands?.map((band) => (
                <SelectItem key={band.id} value={band.id}>{band.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Timeline */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-[#8C8078]" />
            Timeline
          </Label>
          <Select
            value={data.timeline || ''}
            onValueChange={(v) => setData(prev => ({ ...prev, timeline: v }))}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select timeline" />
            </SelectTrigger>
            <SelectContent>
              {options?.timelines?.map((t) => (
                <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Decision Maker */}
        <div className="col-span-2 flex items-center gap-3 p-3 border rounded-lg">
          <Checkbox
            checked={data.decisionMaker || false}
            onCheckedChange={(v) => setData(prev => ({ ...prev, decisionMaker: v }))}
          />
          <div>
            <Label className="flex items-center gap-2 cursor-pointer">
              <User className="w-4 h-4 text-[#8C8078]" />
              Decision Maker
            </Label>
            <p className="text-xs text-[#8C8078]">Contact is the primary decision maker</p>
          </div>
        </div>

        {/* Service Interests */}
        <div className="col-span-2 space-y-2">
          <Label className="flex items-center gap-2">
            <Briefcase className="w-4 h-4 text-[#8C8078]" />
            Service Interests
          </Label>
          <div className="flex flex-wrap gap-2">
            {options?.serviceInterests?.map((service) => {
              const isSelected = (data.serviceInterests || []).includes(service);
              return (
                <button
                  key={service}
                  type="button"
                  onClick={() => {
                    const current = data.serviceInterests || [];
                    const updated = isSelected
                      ? current.filter(s => s !== service)
                      : [...current, service];
                    setData(prev => ({ ...prev, serviceInterests: updated }));
                  }}
                  className={cn(
                    "px-3 py-1.5 rounded-full text-sm border transition-all",
                    isSelected
                      ? "bg-[#4A3B32] text-white border-[#4A3B32]"
                      : "bg-white text-[#5D524D] border-[#E6E0D9] hover:border-[#4A3B32]"
                  )}
                >
                  {service}
                </button>
              );
            })}
          </div>
        </div>

        {/* Notes */}
        <div className="col-span-2 space-y-2">
          <Label>Qualification Notes</Label>
          <Textarea
            value={data.qualificationNotes || ''}
            onChange={(e) => setData(prev => ({ ...prev, qualificationNotes: e.target.value }))}
            placeholder="Additional qualification notes..."
            className="min-h-[80px]"
          />
        </div>
      </div>

      {/* Save Button */}
      <Button
        onClick={handleSave}
        disabled={saving}
        className="w-full bg-[#4A3B32] hover:bg-[#3A2B22]"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
        Save Qualification
      </Button>
    </div>
  );
}

// Compliance Checklist Component
export function ComplianceChecklist({ leadId, compliance, onUpdate }) {
  const { token } = useAuth();
  const [expanded, setExpanded] = useState({});
  const [saving, setSaving] = useState(null);

  const handleToggle = async (itemId, currentValue) => {
    setSaving(itemId);
    try {
      await axios.put(`${API_BASE}/api/leads/compliance/lead/${leadId}/checklist`, {
        itemId,
        completed: !currentValue
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onUpdate?.();
    } catch (error) {
      toast.error('Failed to update checklist');
    } finally {
      setSaving(null);
    }
  };

  const completedItems = compliance?.checklist?.filter(c => c.completed)?.length || 0;
  const completionPct = compliance?.completionPercentage || 0;

  // Default checklist template
  const checklistTemplate = [
    {
      id: "kyc-identity",
      category: "KYC - Identity Verification",
      items: [
        { id: "photo-id", name: "Photo ID Verified", required: true },
        { id: "address-proof", name: "Proof of Address", required: true },
        { id: "pep-check", name: "PEP/Sanctions Check", required: true }
      ]
    },
    {
      id: "disclosures",
      category: "Disclosures & Consent",
      items: [
        { id: "fsg-provided", name: "FSG Provided", required: true },
        { id: "fsg-acknowledged", name: "FSG Acknowledged", required: true },
        { id: "privacy-consent", name: "Privacy Consent", required: true },
        { id: "fee-disclosure", name: "Fee Disclosure", required: true }
      ]
    },
    {
      id: "risk-assessment",
      category: "Risk Assessment",
      items: [
        { id: "risk-profile", name: "Risk Profile Completed", required: true },
        { id: "investment-experience", name: "Investment Experience", required: true },
        { id: "financial-goals", name: "Financial Goals Documented", required: true }
      ]
    }
  ];

  const getItemStatus = (itemId) => {
    return compliance?.checklist?.find(c => c.itemId === itemId)?.completed || false;
  };

  return (
    <div className="space-y-4">
      {/* Progress Header */}
      <div className="p-4 bg-[#F9F7F5] rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-[#2C2420]">Compliance Progress</span>
          <span className={cn(
            "text-sm font-bold",
            completionPct >= 100 ? "text-green-600" : 
            completionPct >= 50 ? "text-amber-600" : "text-gray-500"
          )}>
            {completionPct}%
          </span>
        </div>
        <Progress value={completionPct} className="h-2" />
        <p className="text-xs text-[#8C8078] mt-2">
          {completedItems} items completed
        </p>
      </div>

      {/* Checklist Categories */}
      <div className="space-y-3">
        {checklistTemplate.map((category) => {
          const isExpanded = expanded[category.id] !== false;
          const categoryCompleted = category.items.filter(item => getItemStatus(item.id)).length;
          const categoryTotal = category.items.length;
          const allComplete = categoryCompleted === categoryTotal;
          
          return (
            <div key={category.id} className="border border-[#E6E0D9] rounded-lg overflow-hidden">
              {/* Category Header */}
              <button
                onClick={() => setExpanded(prev => ({ ...prev, [category.id]: !isExpanded }))}
                className="w-full p-3 flex items-center justify-between bg-[#F9F7F5] hover:bg-[#F5F3F1] transition-colors"
              >
                <div className="flex items-center gap-2">
                  {allComplete ? (
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  ) : (
                    <Circle className="w-5 h-5 text-[#8C8078]" />
                  )}
                  <span className="font-medium text-[#2C2420]">{category.category}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-[#8C8078]">{categoryCompleted}/{categoryTotal}</span>
                  {isExpanded ? (
                    <ChevronUp className="w-4 h-4 text-[#8C8078]" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-[#8C8078]" />
                  )}
                </div>
              </button>

              {/* Category Items */}
              {isExpanded && (
                <div className="p-3 space-y-2">
                  {category.items.map((item) => {
                    const isComplete = getItemStatus(item.id);
                    const isSaving = saving === item.id;
                    
                    return (
                      <label
                        key={item.id}
                        className={cn(
                          "flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors",
                          isComplete ? "bg-green-50" : "hover:bg-[#F9F7F5]"
                        )}
                      >
                        {isSaving ? (
                          <Loader2 className="w-5 h-5 animate-spin text-[#4A3B32]" />
                        ) : (
                          <Checkbox
                            checked={isComplete}
                            onCheckedChange={() => handleToggle(item.id, isComplete)}
                          />
                        )}
                        <div className="flex-1">
                          <span className={cn(
                            "text-sm",
                            isComplete ? "text-green-700" : "text-[#2C2420]"
                          )}>
                            {item.name}
                          </span>
                          {item.required && (
                            <span className="ml-1 text-red-500 text-xs">*</span>
                          )}
                        </div>
                        {isComplete && (
                          <CheckCircle2 className="w-4 h-4 text-green-600" />
                        )}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Combined Compliance Panel for Lead Detail
export function LeadCompliancePanel({ leadId, onUpdate }) {
  const { token } = useAuth();
  const [compliance, setCompliance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('qualification');

  useEffect(() => {
    fetchCompliance();
  }, [leadId]);

  const fetchCompliance = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/leads/compliance/lead/${leadId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompliance(response.data);
    } catch (error) {
      console.error('Failed to fetch compliance:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="py-8 text-center">
        <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#4A3B32]" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-[#F5F3F1] rounded-lg">
        <button
          onClick={() => setActiveTab('qualification')}
          className={cn(
            "flex-1 py-2 rounded-md text-sm font-medium transition-all",
            activeTab === 'qualification' 
              ? "bg-white shadow-sm text-[#2C2420]" 
              : "text-[#5D524D]"
          )}
        >
          Qualification
        </button>
        <button
          onClick={() => setActiveTab('compliance')}
          className={cn(
            "flex-1 py-2 rounded-md text-sm font-medium transition-all",
            activeTab === 'compliance' 
              ? "bg-white shadow-sm text-[#2C2420]" 
              : "text-[#5D524D]"
          )}
        >
          Compliance
        </button>
      </div>

      {/* Content */}
      {activeTab === 'qualification' ? (
        <QualificationPanel
          leadId={leadId}
          qualification={compliance?.qualification}
          onUpdate={fetchCompliance}
        />
      ) : (
        <ComplianceChecklist
          leadId={leadId}
          compliance={compliance}
          onUpdate={fetchCompliance}
        />
      )}
    </div>
  );
}

export default LeadCompliancePanel;
